import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import { Shape }                 from '@tokamap/core';
import { EnvironmentOutlined }   from '@ant-design/icons';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import Input                     from '@widesk-ui/components/form/Input';
import PlacePicker               from '@/TokamapEditor/ux/PlacePicker';
import PlaceModel                from '@models/map/PlaceModel';
import useDesignTokens           from '@/hooks/useDesignTokens';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import linkPlaceToShape          from '@/TokamapEditor/helpers/linkPlaceToShape';

const UnlinkedShapePlace = observer(({ shape }: { shape: Shape; }) => {
	const tokens = useDesignTokens();
	const opened = currentEditorStore.promotingShapeId === shape.id;
	const setOpened = (value: boolean) => currentEditorStore.setPromotingShape(value ? shape.id : null);
	const [name, setName] = React.useState('');

	const fresh = currentEditorStore.freshShapeId === shape.id;
	const project = currentProjectEditorStore.get.project;

	const link = (place?: PlaceModel) => {
		if (!place) return;

		currentEditorStore.setPromotingShape(null);
		void linkPlaceToShape([shape.id], place);
	};

	const create = () => leftSidebarStore.openNewPlace(link, name.trim() || undefined);

	if (!fresh && !opened) {
		return (
			<View paddingH="lg" style={{ paddingBottom: 4 }}>
				<Button
					block
					type="dashed"
					icon={<EnvironmentOutlined />}
					onClick={() => setOpened(true)}
					style={{ color: tokens.text.secondary }}
				>
					En faire un {PlaceModel.localize('default', 'l')}
				</Button>
			</View>
		);
	}

	const accent = tokens.color.primary;

	// Visible sans concurrencer « Créer » : fond blanc et bordure franche, plutôt que le gris des
	// boutons de la sidebar, qui le faisait passer inaperçu.
	const secondaryButtonStyle = {
		height: 36,
		padding: '0 12px',
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 600,
		// `elevated` et non `card` : c'est le blanc du fond de la sidebar, `card` y paraît grisé.
		background: tokens.surface.elevated,
		borderColor: tokens.border.default,
		color: tokens.text.default,
		boxShadow: 'none',
	};

	return (
		<View paddingH="lg" pb="lg" gap="lg">
			<View size={13} lineHeight={1.5} color={tokens.text.muted}>
				Un {PlaceModel.localize('default', 'l')}, c'est ce que le visiteur peut chercher sur la carte et
				ouvrir pour en savoir plus : un magasin, une salle, un service… Une forme qui n'en est pas un
				reste un simple dessin.
			</View>

			<View
				p="md"
				gap="sm"
				br={10}
				style={{
					border: `1px solid color-mix(in srgb, ${accent} 35%, transparent)`,
					background: `color-mix(in srgb, ${accent} 6%, transparent)`,
				}}
			>
				<View size={13} style={{ fontWeight: 600 }}>
					C'est un {PlaceModel.localize('default', 'l')} ? Donnez-lui un nom
				</View>

				<View row gap="xs">
					<View flex>
						{/* Pas d'`autoFocus` : on doit pouvoir enchaîner les tracés au clavier. */}
						<Input
							placeholder={`Nom du ${PlaceModel.localize('default', 'l')}`}
							value={name}
							// Hors d'un formulaire, l'`Input` de widesk transmet l'événement d'antd et non la valeur
							// annoncée par son type.
							onChange={(e: unknown) => setName((e as React.ChangeEvent<HTMLInputElement>).target?.value ?? String(e ?? ''))}
							onPressEnter={create}
						/>
					</View>

					<Button type="primary" onClick={create}>Créer</Button>
				</View>

				<View size={12} color={tokens.text.muted}>
					Vous choisirez ensuite son type. Il sera associé à cette forme.
				</View>
			</View>

			<View row>
				{fresh ? (
					<Button style={secondaryButtonStyle} onClick={() => currentEditorStore.markFreshShape(null)}>
						Non, ce n'est pas un {PlaceModel.localize('default', 'l')}
					</Button>
				) : (
					<Button style={secondaryButtonStyle} onClick={() => setOpened(false)}>
						Non, ce n'est pas un {PlaceModel.localize('default', 'l')}
					</Button>
				)}
			</View>

			<View gap={6}>
				<View size={13} style={{ fontWeight: 600 }}>
					Ou l'associer à {PlaceModel.localize('a', 'l')} existant
				</View>

				<PlacePicker projectId={project.id} onPick={link} />
			</View>
		</View>
	);
});

export default UnlinkedShapePlace;
